"use client"

import NextImage from "@/components/common/NextImage"
import { useAppRouter } from "@/hooks/app/useAppRouter"
import { TypeCategory, TypeStoreDetail } from "@/lib/redux/features/store/type"
import { getPageSizeProducts } from "@/utils/category/getPageSizeProducts"
import { getSocailMediaItems } from "@/utils/store/getSocailMediaItems"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Avatar, Button, Col, ConfigProvider, Drawer, Image, List, Row, Space, theme } from "antd"
import { forwardRef, Fragment, useImperativeHandle, useState } from "react"

export type DrawerSideBarRef = {
  close: () => void,
  open: () => void
}

type DrawerSideBarProps = {
  store: TypeStoreDetail,
  categories: TypeCategory[]
}

const DrawerSideBar = forwardRef<DrawerSideBarRef, DrawerSideBarProps>(({ store, categories }, ref) => {
  const [open, setOpen] = useState<boolean>(false)
  const token = theme.useToken().token
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const router = useAppRouter()

  useImperativeHandle(ref, () => {
    return {
      open: handleOpen,
      close: handleClose
    }
  }, [])

  const items = getSocailMediaItems(store)

  return (
    <ConfigProvider
      theme={{
        token: {
          colorText: store.theme.color_text
        }
      }}
    >
      <Drawer
        open={open}
        onClose={handleClose}
        size='large'
        footer={!!items.length && (
          <Row justify='center'>
            <Col>
              <Space>
                {items.map(({ url, icon, type }, idx) => {
                  if (!url || !icon) {
                    return <Fragment key={idx} />
                  }
                  return (
                    <Button
                      styles={{
                        icon: {
                          alignItems: 'center',
                          display: 'flex'
                        }
                      }}
                      onClick={() => window.open(url, '_blank')}
                      size="large"
                      type='text'
                      style={{ fontSize: '2.5rem', fontWeight: 'bold', width: 'auto' }}
                      icon={typeof icon === 'string' ? <NextImage hiddenSpin src={icon} height={60} width={60} alt={`${type} icon`} style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} /> : <FontAwesomeIcon icon={icon} />}
                    />
                  )
                })}
              </Space>
            </Col>
          </Row>
        )}
        title={(
          <Row align='middle' justify='center'>
            <Col style={{ textAlign: 'center' }}>
              {store.logo ? (
                <Row align='middle'>
                  <NextImage
                    hiddenSpin
                    onClick={() => {
                      // gotoHomePage(router, store)
                    }}
                    style={{ cursor: 'pointer', objectFit: 'cover', maxHeight: 30, width: 'auto' }}
                    src={store.logo}
                    height={60}
                    width={60}
                    alt="Logo store in the drawer side bar"
                  />
                </Row>
              ) : store.hidden_store_name ? '' : store.store_name}
            </Col>
          </Row>
        )}
      >
        <List
          dataSource={categories}
          split={false}
          grid={store.category_card_mobile === 'secondary' ? { xs: 2, md: 3, column: 3, gutter: 10 } : undefined}
          renderItem={(item) => {
            return (
              <List.Item
                style={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  const countShownProducts = getPageSizeProducts(store)
                  router.push(countShownProducts >= item.count_product
                    ? `/category/${item.slug}`
                    : `/category/${item.slug}/products/page/1`)

                  handleClose()
                }}
              >
                {store.category_card_mobile === 'secondary' ? (
                  <NextImage
                    src={item.category_image_2nd || "/static/images/cat_square.png"}
                    style={{ objectFit: 'cover', borderRadius: token.borderRadius, width: '100%', height: 'auto' }}
                    alt={item.category_name}
                    width={500}
                    height={500}
                  />
                ) : (
                  <List.Item.Meta
                    avatar={<Avatar src={store.category_card_mobile === 'third' ? item.category_image_3rd : item.category_image} />}
                    title={item.category_name}
                  />
                )}
              </List.Item>
            )
          }}
        />
      </Drawer>
    </ConfigProvider>
  )
})

export default DrawerSideBar